import React, { useEffect, useState } from 'react'

function easeOutExpo(t) {
  return t === 1 ? 1 : 1 - Math.pow(2, -10 * t)
}

function toFiniteNumber(value, fallback = 0) {
  if (value == null) return fallback
  const n = Number(value)
  return Number.isFinite(n) ? n : fallback
}

export function formatCountUpValue(
  value,
  {
    decimals = 0,
    decimal = '.',
    separator = ',',
    prefix = '',
    suffix = '',
  } = {},
) {
  const n = toFiniteNumber(value, 0)
  const fixed = Math.abs(n).toFixed(decimals)
  const [intRaw, frac] = fixed.split('.')
  const withSep =
    separator && separator.length > 0
      ? intRaw.replace(/\B(?=(\d{3})+(?!\d))/g, separator)
      : intRaw
  const body =
    decimals > 0 ? `${withSep}${decimal}${frac}` : withSep
  const signed = n < 0 ? `-${body}` : body
  return `${prefix}${signed}${suffix}`
}

/**
 * Drop-in for `react-countup` (props used in this app).
 * Animates from `start` → `end` with optional delay/easing/formatting.
 */
export default function CountUp({
  start = 0,
  end = 0,
  duration = 2,
  delay = 0,
  decimals = 0,
  decimal = '.',
  separator = ',',
  prefix = '',
  suffix = '',
  useEasing = true,
  className,
  style,
}) {
  const safeStart = toFiniteNumber(start, 0)
  const safeEnd = toFiniteNumber(end, 0)
  const [value, setValue] = useState(safeStart)

  useEffect(() => {
    let rafId = 0
    let timeoutId = 0
    let cancelled = false

    const run = () => {
      const t0 = performance.now()
      const durMs = Math.max(0, Number(duration) || 0) * 1000

      const tick = (now) => {
        if (cancelled) return
        const t = durMs === 0 ? 1 : Math.min(1, (now - t0) / durMs)
        const eased = useEasing ? easeOutExpo(t) : t
        setValue(safeStart + (safeEnd - safeStart) * eased)
        if (t < 1) rafId = requestAnimationFrame(tick)
        else setValue(safeEnd)
      }

      rafId = requestAnimationFrame(tick)
    }

    setValue(safeStart)
    const delayMs = Math.max(0, toFiniteNumber(delay, 0)) * 1000
    if (delayMs > 0) timeoutId = setTimeout(run, delayMs)
    else run()

    return () => {
      cancelled = true
      cancelAnimationFrame(rafId)
      clearTimeout(timeoutId)
    }
  }, [safeStart, safeEnd, duration, delay, useEasing])

  return (
    <span className={className} style={style}>
      {formatCountUpValue(value, {
        decimals,
        decimal,
        separator,
        prefix,
        suffix,
      })}
    </span>
  )
}
